import React, { useMemo, useState } from "react";
import { Button, Container } from "reactstrap";
import { Stack } from "react-bootstrap";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import { ViewIcon } from "@hugeicons/core-free-icons";
import { useDebouncedSearch } from "@utils/hooks/useDebouncedSearch";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { TableC } from "@/CommonElements/TableC";
import { useGetPilgrimVisaGroupsQuery } from "@/services/makkahVisas";
import { SelectCountry } from "@/Component/Form/SelectCountry/SelectCountry";
import { countryCodeMap } from "@/Pages/Campaigns/data";
import { permissions } from "@/utils/constants";
import usePermissions from "@/utils/hooks/usePermissions";
import { useSystem } from "@/utils/hooks/useLocation";

const FlagWithName = ({ countryId, nameAr, nameEn }) => {
  const { getLocalizedText } = useLocalizedText();
  const code = countryId != null ? countryCodeMap[countryId]?.toLowerCase() : null;
  const label = getLocalizedText(nameAr, nameEn) || "-";

  return (
    <Stack direction="horizontal" gap={1} className="align-items-center flex-wrap">
      {code ? (
        <i className={`flag-icon flag-icon-${code}`} aria-hidden />
      ) : null}
      <span>{label}</span>
    </Stack>
  );
};

const Visas = () => {
  const {
    hasAnyPermission
  } = usePermissions();
  const { from_id } = useSystem();
  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedNumber } = useLocalizedText();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});

  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch("", 500);

  const { data: res, isLoading, isFetching } = useGetPilgrimVisaGroupsQuery({
    search: debouncedValue,
    page: currentPage,
    ...filters,
  });

  const rawRows = res?.data?.data;
  const rows = useMemo(() => {
    const list = Array.isArray(rawRows) ? rawRows : rawRows?.data ?? [];
    return list.map((row) => ({
      ...row,
      id: `${row.mission_id}-${row.nationality_id}-${row.country?.id ?? ""}`,
    }));
  }, [rawRows]);

  const meta = res?.data?.meta ?? rawRows?.meta;
  const loading = isLoading || isFetching;
  const pageSize = meta?.per_page ?? 10;

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterApply = (next) => {
    setFilters(next);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        name: t("SERIAL"),
        key: "serial",
        filterable: false,
        render: (_, row, index) =>
          getLocalizedNumber((currentPage - 1) * pageSize + index + 1),
      },
      {
        name: t("VISAS_COL_ORGANIZER"),
        key: "mission",
        render: (_, row) => (
          <FlagWithName
            countryId={row?.mission?.country_id}
            nameAr={row?.mission?.name_ar}
            nameEn={row?.mission?.name_en}
          />
        ),
      },
      {
        name: t("NATIONALITY"),
        key: "nationality",
        render: (_, row) => (
          <FlagWithName
            countryId={row?.nationality?.id}
            nameAr={row?.nationality?.name_ar}
            nameEn={row?.nationality?.name_en}
          />
        ),
      },
      {
        name: t("COUNTRY"),
        key: "country",
        render: (_, row) => (
          <FlagWithName
            countryId={row?.country?.id}
            nameAr={row?.country?.name_ar}
            nameEn={row?.country?.name_en}
          />
        ),
      },
      {
        name: t("VISAS_COL_VISAS_TOTAL"),
        key: "visas_total",
        render: (v) => getLocalizedNumber(v ?? 0),
      },
      {
        name: t("VISAS_COL_VISAS_ACTIVE"),
        key: "visas_active",
        render: (v) => getLocalizedNumber(v ?? 0),
      },
      {
        name: t("VISAS_COL_VISAS_INACTIVE"),
        key: "visas_inactive",
        render: (v) => getLocalizedNumber(v ?? 0),
      },
      hasAnyPermission([permissions.list_pilgrim_visas]) && {
        name: t("ACTIONS"),
        key: "actions",
        filterable: false,
      },
    ].filter(Boolean),
    [t, currentPage, pageSize, getLocalizedNumber]
  );

  const getActions = () => [
    {
      key: "view",
      disabled: true,
      label: (
        <Button
          color="link"
          className="p-0 text-muted d-inline-flex align-items-center gap-1"
          disabled
          type="button"
        >
          <HugeiconsIcon icon={ViewIcon} size={18} />
          <span>{t("VIEW")}</span>
        </Button>
      ),
    },
  ];

  const filterFields = [
    {
      name: "mission_id",
      label: t("ORGANIZER"),
      type: "select",
      optionKey: "missions",
      options: (mission) => ({
        value: mission.id,
        label: getLocalizedText(mission.name_ar, mission.name_en),
      }),
    },
    {
      name: "nationality_id",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "country_id",
      label: t("COUNTRY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "visa_status",
      label: t("STATUS"),
      type: "select",
      options: [
        { label: t("VISAS_STATUS_ACTIVE_VISAS"), value: "active" },
        { label: t("VISAS_STATUS_INACTIVE_VISAS"), value: "inactive" },
      ],
    },
  ];

  return (
    <div>
      <PageHeader title={t("VISAS")} sticky={false} />
      <Container fluid>
        <TableC
          data={rows}
          columns={columns}
          loading={loading}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          searchPlaceholder={t("VISAS_SEARCH_PLACEHOLDER")}
          pagination={{
            current: currentPage || 1,
            total: meta?.total ?? 0,
            pageSize,
            onChange: handlePageChange,
          }}
          actions={getActions}
          filterFields={filterFields}
          filters={filters}
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
          emptyStateTitle={debouncedValue ? t("VISAS_NO_RESULTS") : t("VISAS_NO_DATA")}
          emptyStateDescription=""
          headerKey="mission"
          minVisibleColumns={2}
          skeletonCount={5}
        />
      </Container>
    </div>
  );
};

export default Visas;
